import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';

import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

import DA from '../store/ReduxAccess';

import Modal from './Modal';
import CrmTextField from './CrmTextField';
import KDatePicker from './KDatePicker';
import ReduxLoader from '../store/ReduxLoader';

const styles = {
  errorMessageDiv: {
    marginTop: '8px',
    color: 'red',
    fontWeight: 500,
  },
};

/*
Call this with
    <CampaignModal
        show={the condition under which it should show}
        campaignId={whatever (0 if on initial call page)}
        closeModalFunc={a function for closing the dialog}
    />
*/

class CampaignModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      errorMessage: undefined,
      campaignName: '',
      startDate: DateTime.local().startOf('day'),
      endDate: null,
      campaignDescrip: '',
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.campaignId !== prevProps.campaignId) {
      this.setStateForCampaign(this.props.campaignId);
    }
  }

  setStateForCampaign(campaignId) {
    if (!campaignId) {
      this.clearForm();
    } else {
      const campaign = DA.getCampaignWithId(campaignId);
      this.setState({
        errorMessage: undefined,
        campaignName: campaign.name,
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        campaignDescrip: campaign.description,
      });
    }
  }

  changeCampaignName(newName) {
    this.setState({ campaignName: newName });
  }

  changeDate(propertyName, newTimestamp) {
    this.setState({ [propertyName]: newTimestamp });
  }

  clearFormAndCloseModal() {
    this.clearForm();
    this.props.closeModalFunc();
  }

  clearForm() {
    this.setState({
      errorMessage: undefined,
      campaignName: '',
      startDate: DateTime.local().startOf('day'),
      endDate: null,
      campaignDescrip: '',
    });
  }

  clearErrorValidateAndSubmit(campaignId) {
    this.setState({ errorMessage: '' }, () => this.validateAndSubmit(campaignId));
  }

  validateAndSubmit(campaignId) {
    const validationResult = this.validateForm();
    if (validationResult.valid) {
      this.submit(campaignId);
    } else {
      this.setState({ errorMessage: validationResult.errorMessage });
    }
  }

  submit(campaignId) {
    const firmId = DA.getFirmId();
    const apiStruct = this.buildApiStruct(campaignId);
    const uri = `/api/firm/${firmId}/campaign`;
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      if (campaignId) {
        ReduxLoader.modifyCampaign(apiStruct);
      } else {
        ReduxLoader.addCampaign(json);
      }
      this.clearFormAndCloseModal();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildApiStruct(campaignId) {
    const firmId = DA.getFirmId();
    const { campaignName, startDate, endDate } = this.state;
    return {
      campaignId,
      firmId,
      name: campaignName.trim(),
      startDate: startDate.toISO(),
      endDate: endDate ? endDate.toISO() : '',
      description: this.state.campaignDescrip.trim(),
    };
  }

  validateForm() {
    const { campaignName, startDate, endDate } = this.state;
    if (!campaignName.trim()) {
      return { valid: false, errorMessage: 'You must provide a Campaign Name' };
    }
    if (!startDate) {
      return { valid: false, errorMessage: 'You must provide a Start Date for the campaign' };
    }
    if (endDate) {
      if (endDate.valueOf() < startDate.valueOf()) {
        return { valid: false, errorMessage: 'The End Date must be after the Start Date' };
      }
    }
    /*
    if (!this.state.campaignDescrip.trim()) {
      return { valid: false, errorMessage: "You must provide a Description of the campaign" };
    }
    */
    return { valid: true };
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    const { classes, show, campaignId } = this.props;
    const verb = campaignId ? 'Edit' : 'Create';
    const title = verb + ' Campaign Description';
    const okButtonLabel = campaignId ? 'Save' : 'Create';
    return (
      <Modal
        open={show}
        title={title}
        okButtonLabel={okButtonLabel}
        cancelButtonLabel="Cancel"
        onOk={() => this.clearErrorValidateAndSubmit(campaignId)}
        onCancel={() => this.clearFormAndCloseModal()}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CrmTextField
              label="Campaign Name"
              fullWidth
              value={this.state.campaignName}
              onChange={(event) => this.changeCampaignName(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <KDatePicker
              label="Start Date"
              value={this.state.startDate}
              onChange={(newDate) => this.setState({ startDate: newDate })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <KDatePicker
              label="End Date (optional)"
              value={this.state.endDate}
              onChange={(newDate) => this.setState({ endDate: newDate })}
            />
          </Grid>
          <Grid item xs={12}>
            <CrmTextField
              label="Description"
              fullWidth
              multiline
              value={this.state.campaignDescrip}
              onChange={(event) => this.setState({ campaignDescrip: event.target.value })}
            />
          </Grid>
        </Grid>
        <div className={classes.errorMessageDiv}>{this.state.errorMessage}</div>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({});
const actionProducers = {
  setApiResponseInfo,
};

export default connect(mapStateToProps, actionProducers)(withStyles(styles)(CampaignModal));
